import type { AnySchema, StringSchema } from '@hapi/joi';

import Joi, { MAX_CHARACTERS, characterLimitedString, validEmail } from '../../helpers/joiCommonSchemas';
import { multiSelectDropDownOption, singleSelectDropdownOption } from '../../shared/schemas/dropdown';

const DATE_MESSAGES = {
  'any.required': 'Select a date',
  'date.less': 'The date must be in the past',
  'date.max': 'The date cannot be in the future',
};

const VALIDATION_MESSAGES = {
  INVALID_MOBILE: 'Invalid mobile number',
  FIRST_NAME_REQUIRED: 'First name is required',
  LAST_NAME_REQUIRED: 'Last name is required',
  EMAIL_REQUIRED: 'A valid email is required',
  ADMIN_NOTES_LENGTH: 'Exceeded 1000 characters',
  VISITOR_NOTES_LENGTH: 'Exceeded 4000 characters',
  FIRST_NAME_LENGTH: 'First Name length must be less than or equal to 255 characters long',
  LAST_NAME_LENGTH: 'Last Name length must be less than or equal to 255 characters long',
  COMPANY_LENGTH: 'Company length must be less than or equal to 255 characters long',
  SUBJECT_LENGTH: 'Subject length must be less than or equal to 255 characters long',
};

const EXPIRATION_DATE_MESSAGES = {
  'any.required': 'Select a date',
  'date.min': 'The date must be in the future',
  'date.base': 'Expiry must be a valid date',
  'date.greater': 'The valid to date must be after the valid from date',
};

export const importInvitationsSchema = Joi.object({
  notifyGuests: Joi.bool().required(),
  timezone: singleSelectDropdownOption(),
}).required();

export const exportInvitationsSchema = Joi.object({
  from: Joi.date().required().messages(DATE_MESSAGES),
  to: Joi.date().required().messages(DATE_MESSAGES),
  sites: singleSelectDropdownOption().required(),
  companies: multiSelectDropDownOption().required(),
}).required();

export const inviteeSchema = Joi.object().keys({
  firstName: Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGES.FIRST_NAME_REQUIRED,
    'string.max': VALIDATION_MESSAGES.FIRST_NAME_LENGTH,
  }),
  lastName: Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGES.LAST_NAME_REQUIRED,
    'string.max': VALIDATION_MESSAGES.LAST_NAME_LENGTH,
  }),
  email: validEmail().allow('').messages({
    'string.empty': VALIDATION_MESSAGES.EMAIL_REQUIRED,
    'string.email': VALIDATION_MESSAGES.EMAIL_REQUIRED,
  }),
  mobile: Joi.string()
    .phoneNumber()
    .allow(null, '')
    .messages({ 'string.phoneNumber': VALIDATION_MESSAGES.INVALID_MOBILE }),
});

export function generateCreateInvitationsSchema(isHostRequired: boolean, isEdit: boolean, isSiteZonesEnabled: boolean) {
  return Joi.object()
    .keys({
      company: characterLimitedString().allow('').empty('').messages({
        'string.max': VALIDATION_MESSAGES.COMPANY_LENGTH,
      }),
      subject: characterLimitedString().allow('').messages({ 'string.max': VALIDATION_MESSAGES.SUBJECT_LENGTH }),
      adminNotes: Joi.string().allow('').max(1000).messages({ 'string.max': VALIDATION_MESSAGES.ADMIN_NOTES_LENGTH }),
      siteOption: singleSelectDropdownOption<{ countryCode: StringSchema }>().append({ countryCode: Joi.string() }),
      zone: isSiteZonesEnabled ? singleSelectDropdownOption() : Joi.forbidden(),
      visitorTypeOption: singleSelectDropdownOption<{ visitorType: AnySchema }>().append({ visitorType: Joi.any() }),
      expiry: Joi.object(),
      specific: Joi.date(),
      hostOption: isHostRequired ? singleSelectDropdownOption() : Joi.forbidden(),
      validFrom: Joi.date().messages(DATE_MESSAGES).required(),
      validTo: isEdit
        ? Joi.date().greater(Joi.ref('validFrom')).messages(EXPIRATION_DATE_MESSAGES).required()
        : Joi.date().min('now').messages(EXPIRATION_DATE_MESSAGES).greater(Joi.ref('validFrom')).required(),
      visitorNotes: Joi.string()
        .allow('')
        .max(4000)
        .messages({ 'string.max': VALIDATION_MESSAGES.VISITOR_NOTES_LENGTH }),
      sendEmails: Joi.boolean(),
    })
    .required();
}
